import * as React from "react";
import { useNavigate } from "@opendash/router";
import { AppLayout } from "./AppLayout";
import { useTexts } from "../hooks/useTexts";
import {
  TabContainer,
  TabButton,
  ContentContainer,
  Card,
  ImageContainer,
  FigureCaption,
  SourcesSection,
} from "./common/StyledComponents";
import { LinkifyText } from "./common/LinkifyText";
import { processInlineFormatting } from "./common/RenderText";
import styled from "styled-components";

const b1Image = require("../../assets/b1.png");

// Seitenspezifische Styled Components
const CalloutBox = styled.div`
  background: linear-gradient(135deg, #e3f2fd 0%, #e8eaf6 100%);
  border-left: 4px solid #5c6bc0;
  padding: 20px;
  margin: 20px 0;
  border-radius: 0 8px 8px 0;

  &::before {
    content: "HINWEIS";
    display: block;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: #5c6bc0;
    opacity: 0.6;
    margin-bottom: 8px;
  }

  h4 {
    color: #283593;
    margin-bottom: 10px;
  }

  p {
    margin-bottom: 8px;
  }
`;

const VideoLinkCard = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px 20px;
  margin: 12px 0;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.3s ease;

  &:hover {
    background: #e3f2fd;
    transform: translateX(5px);
  }

  a {
    color: var(--primaryColor);
    font-weight: 500;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

export const WasserwerkRoute = React.memo(function WasserwerkRoute() {
  const navigate = useNavigate();
  const { t, getKeysWithPrefix } = useTexts();

  const literaturNumbers = React.useMemo(() => {
    const textKeys = getKeysWithPrefix("B.1.literatur.");
    const numbers = new Set<number>();
    textKeys.forEach((key) => {
      const match = key.match(/^B\.1\.literatur\.(\d+)\./);
      if (match) {
        numbers.add(parseInt(match[1], 10));
      }
    });
    return Array.from(numbers).sort((a, b) => a - b);
  }, [getKeysWithPrefix]);

  const renderParagraphs = (text: string) => {
    if (!text) return null;
    return text
      .split("\n\n")
      .map((paragraph, index) => (
        <p key={index}>{processInlineFormatting(paragraph)}</p>
      ));
  };

  const tabs = [
    {
      title: t("B.tab.1.title", "Wie funktioniert ein Wasserwerk?"),
      path: "/wasserklaerer/wasseraufbereitung/wasserwerk",
      isActive: true,
    },
    {
      title: t("B.tab.2.title", "Trinkwasserqualität"),
      path: "/wasserklaerer/wasseraufbereitung/qualitaet",
      isActive: false,
    },
    {
      title: t("B.tab.3.title", "Verteilnetz des Trinkwassers"),
      path: "/wasserklaerer/wasseraufbereitung/verteilnetz",
      isActive: false,
    },
  ];

  return (
    <AppLayout>
      <ContentContainer>
        <h1>{t("B.1.title", "Wie funktioniert ein Wasserwerk?")}</h1>

        <TabContainer>
          {tabs.map((tab, index) => (
            <TabButton
              key={index}
              isActive={tab.isActive}
              onClick={() => !tab.isActive && navigate(tab.path)}
            >
              {tab.title}
            </TabButton>
          ))}
        </TabContainer>

        {/* Abschnitt 1: Unterschiedliche Wasseraufbereitung */}
        <Card>
          <h2>
            {t(
              "B.1.aufbereitung.title",
              "Unterschiedliche Wasseraufbereitung je nach Versorger"
            )}
          </h2>
          {renderParagraphs(t("B.1.aufbereitung.text", ""))}
        </Card>

        {/* Abschnitt 2: Enteisenung und Entmanganung */}
        <Card>
          <h2>{t("B.1.enteisenung.title", "Enteisenung und Entmanganung")}</h2>
          {renderParagraphs(t("B.1.enteisenung.text", ""))}

          <ImageContainer>
            <img src={b1Image} alt="Enteisenung und Entmanganung" />
            <FigureCaption>
              Abbildung 1:{" "}
              {t(
                "B.1.enteisenung.image.caption",
                "Enteisenung und Entmanganung (eigene Darstellung, Illustration)"
              )}
            </FigureCaption>
          </ImageContainer>

          <h3>{t("B.1.enteisenung.videos.title", "Erklärvideos")}</h3>

          <VideoLinkCard>
            <a
              href={t(
                "B.1.enteisenung.video1.url",
                "https://www.youtube.com/watch?v=example1"
              )}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t(
                "B.1.enteisenung.video1.label",
                "Erklärvideo der Berliner Wasserbetriebe"
              )}
            </a>
          </VideoLinkCard>

          <VideoLinkCard>
            <a
              href={t(
                "B.1.enteisenung.video2.url",
                "https://www.youtube.com/watch?v=8dYm93he1Kk"
              )}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t(
                "B.1.enteisenung.video2.label",
                "Erklärvideo Wasserwerk Rhede"
              )}
            </a>
          </VideoLinkCard>
        </Card>

        {/* Abschnitt 3: Spurenstoffe */}
        <Card>
          <h2>
            {t("B.1.spurenstoffe.title", "Entfernung organischer Spurenstoffe")}
          </h2>
          {renderParagraphs(t("B.1.spurenstoffe.text", ""))}

          <CalloutBox>
            <h4>
              {t("B.1.spurenstoffe.warnung.title", "Also ist alles gut?")}
            </h4>
            {renderParagraphs(t("B.1.spurenstoffe.warnung.text", ""))}
          </CalloutBox>
        </Card>

        {/* Abschnitt 4: Desinfektion */}
        <Card>
          <h2>
            {t(
              "B.1.desinfektion.title",
              "Mikrobiologische Sicherheit des Wassers"
            )}
          </h2>
          {renderParagraphs(t("B.1.desinfektion.text", ""))}
        </Card>

        {/* Quellenverzeichnis */}
        <SourcesSection>
          <h3>{t("B.1.literatur.title", "Literatur")}</h3>
          <ol>
            {literaturNumbers.map((num) => (
              <li key={num} id={`quelle-${num}`}>
                <LinkifyText>
                  {[
                    t(`B.1.literatur.${num}.text`, ""),
                    t(`B.1.literatur.${num}.url`, ""),
                  ]
                    .filter(Boolean)
                    .join(" ")}
                </LinkifyText>
              </li>
            ))}
          </ol>
        </SourcesSection>
      </ContentContainer>
    </AppLayout>
  );
});
